import { Spreadsheet } from '@/components/spreadsheet/spreadsheet';
import * as _ from 'lodash';
import moment from 'moment';

export class DapSpreadsheetInfo extends Spreadsheet {
  name: string = 'dap_info';

  mount(dataSource: any) {
    const sectionColumns: any = {
      main: [
        { key: 'name', text: 'NAME' },
        { key: 'familyName', text: 'FAMILY' },
        { key: 'segmentName', text: 'SEGMENT' },
        { key: 'initialHistoryPeriod', text: 'START DATE' },
        { key: 'finalHistoryPeriod', text: 'END DATE' }
      ],
      saleTargets: [
        ..._.map(dataSource.saleTargets as any[], () => ({
          key: 'value',
          text: 'VALUE',
          format: '#,0'
        }))
      ],
      subProjectLimits: [
        ..._(dataSource.subProjectLimits as any[])
          .map(() => {
            return [
              { key: 'limitPercentInferior', text: 'INFERIOR', format: '#0.00%' },
              { key: 'limitPercentSuperior', text: 'SUPERIOR', format: '#0.00%' },
              { key: 'marketShareInferior', text: 'INFERIOR', format: '#0.00%' },
              { key: 'marketShareMedium', text: 'MEDIUM', format: '#0.00%' },
              { key: 'marketShareSuperior', text: 'SUPERIOR', format: '#0.00%' }
            ];
          })
          .flatten()
          .value()
      ],
      marketShareInfluence: [
        ..._(dataSource.marketShareInfluence as any[])
          .map(() => {
            return [
              { key: 'roleLimitInferior', text: 'INFERIOR' },
              { key: 'percentIncreaseLimitInferior', text: 'INCREASE', format: '#0.0%' },
              { key: 'roleLimitMediumInferior', text: 'MEDIUM INFERIOR' },
              { key: 'percentIncreaseLimitMediumInferior', text: 'INCREASE', format: '#0.0%' },
              { key: 'roleLimitMedium', text: 'MEDIUM SUPERIOR' },
              { key: 'percentIncreaseLimitMedium', text: 'INCREASE', format: '#0.0%' },
              { key: 'roleLimitSuperior', text: 'SUPERIOR' },
              { key: 'percentIncreaseLimitSuperior', text: 'INCREASE', format: '#0.0%' }
            ];
          })
          .flatten()
          .value()
      ],
      segmentsInfluence: [
        ..._.map(dataSource.segmentsInfluence, (d: any) => ({
          key: 'value',
          text: d.text,
          format: '#0.0%'
        }))
      ],
      planInfluence: [
        ..._(dataSource.planInfluence as any[])
          .map(() => {
            return [
              { key: 'percentProjection', text: 'PROJECTION', format: '#0.0%' },
              { key: 'percentMtdMktShareInfluence', text: 'MS INFLUENCE', format: '#0.0%' },
              { key: 'percentMtdSegmentInfluence', text: 'SEG INFLUENCE', format: '#0.0%' }
            ];
          })
          .flatten()
          .value()
      ],
      industryProjection: [
        ..._.map(dataSource.industryProjection as any[], (d: any) => ({
          key: 'value',
          text: d.nameSegment,
          format: '#,0'
        }))
      ]
    };

    const sections: Array<any> = [
      { key: 'main', text: '', index: 0 },
      { key: 'saleTargets', text: 'SALES TARGET', index: sectionColumns.main.length },
      {
        key: 'subProjectLimits',
        text: 'PROJECT LIMITS',
        index: sectionColumns.main.length + sectionColumns.saleTargets.length
      },
      {
        key: 'marketShareInfluence',
        text: 'MARKET SHARE INFLUENCE',
        index: sectionColumns.main.length + sectionColumns.saleTargets.length + sectionColumns.subProjectLimits.length
      },
      {
        key: 'segmentsInfluence',
        text: 'SEGMENT INFLUENCE',
        index:
          sectionColumns.main.length +
          sectionColumns.saleTargets.length +
          sectionColumns.subProjectLimits.length +
          sectionColumns.marketShareInfluence.length
      },
      {
        key: 'planInfluence',
        text: 'PLAN INFLUENCE',
        index:
          sectionColumns.main.length +
          sectionColumns.saleTargets.length +
          sectionColumns.subProjectLimits.length +
          sectionColumns.marketShareInfluence.length +
          sectionColumns.segmentsInfluence.length
      },
      {
        key: 'industryProjection',
        text: 'FORECAST INDUSTRY',
        index:
          sectionColumns.main.length +
          sectionColumns.saleTargets.length +
          sectionColumns.subProjectLimits.length +
          sectionColumns.marketShareInfluence.length +
          sectionColumns.segmentsInfluence.length +
          sectionColumns.planInfluence.length
      }
    ];

    const sectionColumnsDateTime: any = {
      main: [{ key: 'dealerNumber', text: '' }],
      saleTargets: [
        ..._(dataSource.saleTargets)
          .map((d: any, i: number) => ({
            key: 'yearReference',
            text: String(d.yearReference),
            index: sections[1].index + i
          }))
          .uniqBy((d: any) => d.text)
          .value()
      ],
      subProjectLimits: [
        ..._(dataSource.subProjectLimits)
          .map((d: any, i: number) => ({ key: 'name', text: d.name, index: sections[2].index + i }))
          .uniqBy((d: any) => d.text)
          .value()
      ],
      marketShareInfluence: [
        ..._(dataSource.marketShareInfluence)
          .map((d: any, i: number) => ({ key: 'name', text: d.name, index: sections[3].index + i }))
          .uniqBy((d: any) => d.text)
          .value()
      ],
      segmentsInfluence: [
        ..._(dataSource.segmentsInfluence)
          .map((d: any, i: number) => ({ key: 'name', text: d.name, index: sections[4].index + i }))
          .uniqBy((d: any) => d.text)
          .value()
      ],
      planInfluence: [
        ..._(dataSource.planInfluence)
          .map((d: any, i: number) => ({ key: 'projectName', text: d.projectName, index: sections[5].index + i * 3 }))
          .uniqBy((d: any) => d.text)
          .value()
      ],
      industryProjection: [
        ..._(dataSource.industryProjection)
          .map((d: any, i: number) => ({
            key: 'year',
            text: String(d.year),
            index: sections[6].index + i,
            totalItem: dataSource.industryProjection.filter(function(f) {
              return f.year == d.year;
            }).length
          }))
          .uniqBy((d: any) => d.text)
          .value()
      ]
    };

    const sectionColumnsDescription: any = {
      main: [{ key: 'dealerNumber', text: '' }],
      saleTargets: [
        ..._(dataSource.saleTargets)
          .map((d: any, i: number) => ({
            key: 'monthReference',
            text: String(d.monthReference),
            index: sections[1].index + i
          }))
          .value()
      ],
      subProjectLimits: [
        { key: '', text: 'LIMITES', index: sections[2].index },
        { key: '', text: 'MARKET SHARE', index: sections[2].index + 2 }
      ],
      marketShareInfluence: [
        ...(dataSource.marketShareInfluence.length
          ? [
              { key: '', text: 'A', index: sections[3].index },
              { key: '', text: 'B', index: sections[3].index + 2 },
              { key: '', text: 'C', index: sections[3].index + 4 },
              { key: '', text: 'D', index: sections[3].index + 6 }
            ]
          : [])
      ],
      segmentsInfluence: [
        ...(dataSource.segmentsInfluence.length ? [{ key: '', text: '', index: sections[4].index }] : [])
      ],
      planInfluence: [...(dataSource.planInfluence.length ? [{ key: '', text: '', index: sections[5].index }] : [])],
      industryProjection: [
        ...(dataSource.industryProjection.length ? [{ key: '', text: '', index: sections[6].index }] : [])
      ]
    };

    const baseStyle: any = {
      textAlign: 'left',
      fontSize: 9,
      verticalAlign: 'center',
      fontFamily: 'Calibri'
    };

    const baseHeaderStyle: any = {
      color: '#212121',
      fontSize: 9,
      bold: true,
      textAlign: 'center',
      verticalAlign: 'center',
      fontFamily: 'Calibri'
    };

    const baseColorStyle: any = {
      main: '#bdbdbd',
      subProjectLimits: '#e57373',
      marketShareInfluence: '#90a4ae',
      segmentsInfluence: '#e57373',
      planInfluence: '#90a4ae',
      grey: '#d1d1d1',
      greyDarken: '#c1c1c1',
      saleTargets: '#ffb74d',
      green: '#81c784',
      industryProjection: '#81c784'
    };

    const baseBorderColorStyle: any = {
      main: '#e0e0e0',
      subProjectLimits: '#ffcdd2',
      marketShareInfluence: '#cfd8dc',
      segmentsInfluence: '#ffcdd2',
      planInfluence: '#cfd8dc',
      saleTargets: '#ffe0b2',
      green: '#c8e6c9',
      industryProjection: '#c8e6c9'
    };

    const baseBackgroundColorStyle: any = {
      main: '#fafafa',
      subProjectLimits: '#fff5f6',
      marketShareInfluence: '#f9fafb',
      segmentsInfluence: '#fff5f6',
      planInfluence: '#f9fafb',
      saleTargets: '#fffbf5',
      green: '#f4f9f5',
      industryProjection: '#f4f9f5'
    };

    const headerStyle = _.reduce(
      baseColorStyle,
      (acc: any, val: string, key: string) => {
        acc[key] = _.assign({}, baseHeaderStyle, { background: baseColorStyle[key] });
        return acc;
      },
      {}
    );

    const getCellStyle = (key: any) => {
      return _.assign({}, baseStyle, {
        textAlign: 'center',
        background: baseBackgroundColorStyle[key],
        borderLeft: { color: baseBorderColorStyle[key], size: 1 },
        borderRight: { color: baseBorderColorStyle[key], size: 1 },
        borderTop: { color: baseBorderColorStyle[key], size: 1 },
        borderBottom: { color: baseBorderColorStyle[key], size: 1 }
      });
    };

    this.workbookOptions = {
      sheets: _.map(
        [[dataSource]],
        (data: any, name: string) =>
          ({
            name: `PARAMETERS`,
            frozenColumns: 2,
            frozenRows: 4,
            rows: [
              {
                cells: [
                  ..._.map(sections, (section: any) =>
                    _.assign({}, headerStyle[section.key], {
                      value: section.text,
                      index: section.index,
                      borderLeft: { color: baseBorderColorStyle[section.key], size: 1 },
                      borderRight: { color: baseBorderColorStyle[section.key], size: 1 }
                    })
                  )
                ]
              },
              {
                cells: [
                  ..._(sectionColumnsDateTime)
                    .map((columns: any, key: any) =>
                      _.map(columns, (cell: any) =>
                        _.assign({}, getCellStyle(cell.color || key), {
                          value: cell.text,
                          bold: true,
                          index: cell.index,
                          background: baseColorStyle.grey,
                          borderLeft: { color: baseBorderColorStyle.main, size: 1 },
                          borderRight: { color: baseBorderColorStyle.main, size: 1 },
                          borderTop: { color: baseBorderColorStyle.main, size: 1 },
                          borderBottom: { color: baseBorderColorStyle.main, size: 1 }
                        })
                      )
                    )
                    .flatten()
                    .value()
                ]
              },
              {
                cells: [
                  ..._(sectionColumnsDescription)
                    .map((columns: any, key: any) =>
                      _.map(columns, (cell: any) =>
                        _.assign({}, getCellStyle(cell.color || key), {
                          value: cell.text,
                          index: cell.index,
                          bold: true,
                          background: baseColorStyle.greyDarken,
                          borderLeft: { color: baseBorderColorStyle.main, size: 1 },
                          borderRight: { color: baseBorderColorStyle.main, size: 1 },
                          borderTop: { color: baseBorderColorStyle.main, size: 1 },
                          borderBottom: { color: baseBorderColorStyle.main, size: 1 }
                        })
                      )
                    )
                    .flatten()
                    .value()
                ]
              },
              {
                height: 80,
                cells: [
                  ..._(sectionColumns)
                    .map((columns: any, key: any) =>
                      _.map(columns, (cell: any) =>
                        _.assign({}, headerStyle[cell.color || key], {
                          value: cell.text,
                          wrap: true,
                          borderLeft: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                          borderRight: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                          borderTop: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                          borderBottom: { color: baseBorderColorStyle[cell.color || key], size: 1 }
                        })
                      )
                    )
                    .flatten()
                    .value()
                ]
              },
              ..._.map(data, d => {
                return {
                  cells: [
                    ..._(sectionColumns)
                      .map((columns: any, key: any) => {
                        if (
                          ['subProjectLimits', 'marketShareInfluence', 'planInfluence', 'industryProjection'].some(
                            p => key === p
                          )
                        ) {
                          return _.reduce(
                            d[key],
                            (acc: any[], val: any) => {
                              _.forEach(_.uniqBy(columns, (c: any) => c.key), (cell: any) => {
                                acc.push(
                                  _.assign({}, getCellStyle(cell.color || key), {
                                    value: val[cell.key],
                                    format: cell.format
                                  })
                                );
                              });
                              return acc;
                            },
                            []
                          );
                        }
                        return _.map(columns, (cell: any, index: number) => {
                          if (['saleTargets', 'segmentsInfluence'].some(p => key === p)) {
                            return _.assign({}, getCellStyle(cell.color || key), {
                              value: d[key][index][cell.key],
                              format: cell.format
                            });
                          }

                          if (['initialHistoryPeriod', 'finalHistoryPeriod'].some(p => cell.key == p)) {
                            return _.assign({}, getCellStyle(cell.color || key), {
                              value: moment(d[cell.key]).format('MM/YYYY')
                            });
                          }

                          return _.assign({}, getCellStyle(cell.color || key), {
                            value: d[cell.key],
                            format: cell.format
                          });
                        });
                      })
                      .flatten()
                      .value()
                  ]
                };
              })
            ],
            columns: [
              {
                index: 0,
                width: 100
              },
              {
                index: 1,
                width: 160
              },
              {
                index: 2,
                width: 100
              },
              {
                index: 3,
                width: 100
              },
              {
                index: 4,
                width: 100
              },
              ..._.map(_.range(0, sections[5].index), index => ({
                index: index + 5,
                width: 75
              }))
            ],
            showGridLines: false,
            mergedCells: [
              `A1:${this.columnIndex[sectionColumns.main.length - 1]}1`,
              `A2:${this.columnIndex[sectionColumns.main.length - 1]}2`,
              `A3:${this.columnIndex[sectionColumns.main.length - 1]}3`,
              ...(sectionColumnsDateTime.saleTargets.length
                ? [`${this.columnIndex[sections[1].index]}1:${this.columnIndex[sections[2].index - 1]}1`]
                : []),
              ...(sectionColumnsDateTime.saleTargets.length
                ? _.map(
                    sectionColumnsDateTime.saleTargets,
                    (c: any) =>
                      `${this.columnIndex[c.index]}2:${
                        this.columnIndex[
                          c.index -
                            1 +
                            (_(dataSource.saleTargets)
                              .groupBy((d: any) => d.yearReference)
                              .find((val: any, key: string) => +key === +c.text) as any).length
                        ]
                      }2`
                  )
                : []),
              ...(sectionColumnsDateTime.subProjectLimits.length
                ? [`${this.columnIndex[sections[2].index]}1:${this.columnIndex[sections[3].index - 1]}1`]
                : []),
              ...(sectionColumnsDateTime.subProjectLimits.length
                ? [`${this.columnIndex[sections[2].index]}3:${this.columnIndex[sections[2].index + 1]}3`]
                : []),
              ...(sectionColumnsDateTime.subProjectLimits.length
                ? [`${this.columnIndex[sections[2].index + 2]}3:${this.columnIndex[sections[2].index + 4]}3`]
                : []),
              ...(sectionColumnsDateTime.subProjectLimits.length
                ? _.map(
                    sectionColumnsDateTime.subProjectLimits,
                    (c: any) => `${this.columnIndex[c.index]}2:${this.columnIndex[c.index + 4]}2`
                  )
                : []),
              ...(sectionColumnsDateTime.marketShareInfluence.length
                ? [`${this.columnIndex[sections[3].index]}1:${this.columnIndex[sections[4].index - 1]}1`]
                : []),
              ...(sectionColumnsDateTime.marketShareInfluence.length
                ? [`${this.columnIndex[sections[3].index + 0]}3:${this.columnIndex[sections[3].index + 1]}3`]
                : []),
              ...(sectionColumnsDateTime.marketShareInfluence.length
                ? [`${this.columnIndex[sections[3].index + 2]}3:${this.columnIndex[sections[3].index + 3]}3`]
                : []),
              ...(sectionColumnsDateTime.marketShareInfluence.length
                ? [`${this.columnIndex[sections[3].index + 4]}3:${this.columnIndex[sections[3].index + 5]}3`]
                : []),
              ...(sectionColumnsDateTime.marketShareInfluence.length
                ? [`${this.columnIndex[sections[3].index + 6]}3:${this.columnIndex[sections[3].index + 7]}3`]
                : []),
              ...(sectionColumnsDateTime.marketShareInfluence.length
                ? _.map(
                    sectionColumnsDateTime.marketShareInfluence,
                    (c: any) => `${this.columnIndex[c.index]}2:${this.columnIndex[c.index + 7]}2`
                  )
                : []),
              ...(sectionColumnsDateTime.segmentsInfluence.length
                ? [`${this.columnIndex[sections[4].index]}1:${this.columnIndex[sections[5].index - 1]}1`]
                : []),
              ...(sectionColumnsDateTime.segmentsInfluence.length
                ? [`${this.columnIndex[sections[4].index]}3:${this.columnIndex[sections[5].index - 1]}3`]
                : []),
              ...(sectionColumnsDateTime.segmentsInfluence.length
                ? _.map(
                    sectionColumnsDateTime.segmentsInfluence,
                    (c: any) =>
                      `${this.columnIndex[c.index]}2:${
                        this.columnIndex[
                          c.index -
                            1 +
                            (_(dataSource.segmentsInfluence)
                              .groupBy((d: any) => d.name)
                              .find((val: any, key: string) => key === c.text) as any).length
                        ]
                      }2`
                  )
                : []),
              ...(sectionColumnsDateTime.planInfluence.length
                ? [
                    `${this.columnIndex[sections[5].index]}1:${
                      this.columnIndex[sections[5].index + sectionColumnsDateTime.planInfluence.length * 3 - 1]
                    }1`
                  ]
                : []),
              ...(sectionColumnsDateTime.planInfluence.length
                ? [
                    `${this.columnIndex[sections[5].index]}3:${
                      this.columnIndex[sections[5].index + sectionColumnsDateTime.planInfluence.length * 3 - 1]
                    }3`
                  ]
                : []),
              ...(sectionColumnsDateTime.planInfluence.length
                ? _.map(
                    sectionColumnsDateTime.planInfluence,
                    (c: any) => `${this.columnIndex[c.index]}2:${this.columnIndex[c.index + 2]}2`
                  )
                : []),

              ...(sectionColumnsDateTime.industryProjection.length
                ? [
                    `${this.columnIndex[sections[6].index]}1:${
                      this.columnIndex[sections[6].index + sectionColumns.industryProjection.length - 1]
                    }1`
                  ]
                : []),
              ...(sectionColumnsDateTime.industryProjection.length
                ? [
                    `${this.columnIndex[sections[6].index]}3:${
                      this.columnIndex[sections[6].index + sectionColumns.industryProjection.length - 1]
                    }3`
                  ]
                : []),
              ...(sectionColumnsDateTime.industryProjection.length
                ? _.map(
                    sectionColumnsDateTime.industryProjection,
                    (c: any) => `${this.columnIndex[c.index]}2:${this.columnIndex[c.index + c.totalItem - 1]}2`
                  )
                : [])
            ]
          } as any)
      )
    };
    return this;
  }
}
